// Supabase-backed replacement for the artifact's window.storage dataStore.
//
// The artifact's API surface is preserved so the rest of FinancePlatform.tsx
// doesn't need surgery:
//   list(prefix)              -> records[]     (with name-string fields synthesized)
//   get(key)                  -> record | null
//   set(key, obj)             -> void          (upsert; throws on failure)
//   remove(key)               -> void          (hard delete)
//   clear(prefix)             -> void          (hard delete all rows for this user)
//   fetchAllByPrefix(prefix)  -> records[]
//
// Differences from the window.storage version:
//   - list() returns full records, not keys. clear()/fetchAllByPrefix() were
//     the only callers that needed the keys-then-get pattern, and both are
//     happier with records anyway.
//   - The _schemaVersion key is intercepted and reports a version above what
//     the artifact looks for, so the client-side B1a/B2 seed-and-backfill
//     paths are skipped (M3 already seeded everything server-side).
//   - Currency conversion fields (amount_base, fx_rate) are filled in for
//     transactions on write if missing, using the latest exchange_rates row.
//   - Name-string fields the artifact still reads (tx.account, tx.category,
//     tx.fromAccount, tx.toAccount, asset.linkedLoanId, etc.) are synthesized
//     on read via PostgREST embedded selects against the FK targets.
//
// The DB column names are snake_case; the artifact uses camelCase. Conversion
// happens at the dataStore boundary so callers never see snake_case.

import { supabase } from './supabase';

// ============ prefix → table mapping ============

type Prefix =
  | 'tx:' | 'acc:' | 'card:' | 'loan:' | 'asset:' | 'inv:'
  | 'rec:' | 'bud:' | 'cat:' | 'curr:' | 'fx:' | 'ci:'
  | 'sg:' | 'sga:' | 'rs:' | 'nw:'
  | 'sit:' | 'smr:' | 'mcm:' | 'sra:' | 'cst:'
  | 'bt:' | 'bo:';

const PREFIX_TO_TABLE: Record<Prefix, string> = {
  'tx:': 'transactions',
  'acc:': 'accounts',
  'card:': 'cards',
  'loan:': 'loans',
  'asset:': 'assets',
  'inv:': 'investments',
  'rec:': 'recurring_rules',
  'bud:': 'budgets',
  'cat:': 'categories',
  'curr:': 'currencies',
  'fx:': 'exchange_rates',
  'ci:': 'card_installments',
  'sg:': 'savings_goals',
  'sga:': 'savings_goal_allocations',
  'rs:': 'reimbursement_settlements',
  'nw:': 'net_worth_snapshots',
  'sit:': 'sms_ingest_tokens',
  'smr:': 'sms_ingest_raw',
  'mcm:': 'merchant_category_map',
  'sra:': 'sms_reconciliation_alerts',
  'cst:': 'card_statements',
  'bt:': 'budget_template_lines',
  'bo:': 'budget_overrides',
};

// Tables that don't carry a deleted_at column. dataStore.list skips the
// "live rows only" filter for these. Currently just net_worth_snapshots —
// derived data we hard-overwrite via cron, soft-delete makes no sense.
const TABLES_WITHOUT_DELETED_AT = new Set<string>([
  'net_worth_snapshots',
  'sms_ingest_tokens',
  'sms_ingest_raw',
  'merchant_category_map',
  'sms_reconciliation_alerts',
]);

const READ_ONLY_PREFIXES = new Set<Prefix>(['curr:', 'fx:']);

// Most tables PK on `id` (uuid). currencies PKs on `code` (text).
const pkColumn = (table: string) => (table === 'currencies' ? 'code' : 'id');

function splitKey(key: string): { prefix: Prefix | null; pkValue: string } {
  for (const p of Object.keys(PREFIX_TO_TABLE) as Prefix[]) {
    if (key.startsWith(p)) {
      return { prefix: p, pkValue: key.slice(p.length) };
    }
  }
  return { prefix: null, pkValue: key };
}

// ============ field name conversion ============
// Most fields go through generic camel↔snake. A few non-mechanical pairs
// (artifact's `currency` <-> DB's `currency_code`, etc.) live in OVERRIDES.

const camelToSnake = (s: string) => s.replace(/([A-Z])/g, '_$1').toLowerCase();
const snakeToCamel = (s: string) => s.replace(/_([a-z])/g, (_, c) => c.toUpperCase());

// Per-table renames keyed by the artifact-side field name.
const TO_DB_RENAMES: Record<string, Record<string, string>> = {
  accounts:        { currency: 'currency_code' },
  cards:           { currency: 'currency_code', lastFour: 'last_four', creditLimit: 'credit_limit', currentBalance: 'opening_balance', statementBalance: 'statement_balance', minimumPayment: 'minimum_payment', statementDay: 'statement_day', paymentDueDay: 'payment_due_day', annualFee: 'annual_fee' },
  loans:           { currency: 'currency_code', loanType: 'loan_type', originalPrincipal: 'original_principal', currentBalance: 'opening_balance', interestRate: 'interest_rate', termMonths: 'term_months', monthlyPayment: 'monthly_payment', startDate: 'start_date', paymentDay: 'payment_day' },
  assets:          { currency: 'currency_code', assetType: 'asset_type', purchasePrice: 'purchase_price', purchaseDate: 'purchase_date', appreciationRate: 'appreciation_rate', linkedLoanId: 'linked_loan_id' },
  investments:     { currency: 'currency_code', avgCost: 'avg_cost', currentPrice: 'current_price', priceUpdatedAt: 'price_updated_at' },
  transactions:    { currency: 'currency_code', isRefund: 'is_refund', isReimbursable: 'is_reimbursable', reimbursedAmount: 'reimbursed_amount', reimbursedAt: 'reimbursed_at', reimbursedByTxId: 'reimbursed_by_tx_id', isReimbursement: 'is_reimbursement', reimbursementPayer: 'reimbursement_payer', recurringId: 'recurring_id', occurrenceDate: 'occurrence_date' },
  recurring_rules: { currency: 'currency_code', dayOfMonth: 'day_of_month', nthLastWorkingDay: 'nth_last_working_day', monthOfYear: 'month_of_year', startDate: 'start_date', endDate: 'end_date', linkedCardInstallmentId: 'linked_card_installment_id', materializedThrough: 'materialized_through', isInterestRule: 'is_interest_rule', interestAnnualRatePct: 'interest_annual_rate_pct', maxWithdrawalsPerMonth: 'max_withdrawals_per_month' },
  budgets:         {},
  categories:      {},
  // Budget engine: all fields are mechanical camel<->snake. Month-like date
  // columns (effective_from, month) are 'YYYY-MM' <-> 'YYYY-MM-01' coerced below.
  budget_template_lines: {},
  budget_overrides:      {},
  card_installments: {
    currency: 'currency_code',
    cardId: 'card_id',
    originalAmount: 'original_amount',
    remainingBalance: 'remaining_balance',
    interestRate: 'interest_rate',
    termMonths: 'term_months',
    monthlyPayment: 'monthly_payment',
    startDate: 'start_date',
    paymentDay: 'payment_day',
    feeAmount: 'fee_amount',
    linkedTxId: 'linked_tx_id',
    linkedSourceCardId: 'linked_source_card_id',
    linkedSourceAccountId: 'linked_source_account_id',
    lastDecrementedThrough: 'last_decremented_through',
  },
  savings_goals: {
    currency: 'currency_code',
    targetAmount: 'target_amount',
    targetDate: 'target_date',
  },
  savings_goal_allocations: {
    goalId: 'goal_id',
    accountId: 'account_id',
    investmentId: 'investment_id',
    allocationType: 'allocation_type',
    startDate: 'start_date',
  },
  reimbursement_settlements: {
    incomeTxId: 'income_tx_id',
    expenseTxId: 'expense_tx_id',
  },
  // accounts.opening_balance: the artifact's AccountModal uses `balance` for this column.
};

// accounts uses `balance` in the modal but `opening_balance` in the DB.
TO_DB_RENAMES.accounts.balance = 'opening_balance';

// The artifact carries a few name-string fields alongside the IDs (account,
// category, fromAccount, toAccount) for legacy display. They have no DB
// column; strip on write. The values get synthesized again on read.
const STRIP_ON_WRITE: Record<string, Set<string>> = {
  // 'source' is an artifact-only tag ('manual' | 'recurring') the DB doesn't carry.
  // The reimbursed_* fields on transactions are maintained by a DB trigger
  // (recompute_reimbursed_amount, migration 0010) — letting the artifact write
  // them would race with the trigger and overwrite the canonical value.
  transactions:    new Set(['account', 'category', 'fromAccount', 'toAccount', 'source', 'reimbursedAmount', 'reimbursedAt', 'reimbursedByTxId']),
  // The Recurring list component decorates each rule with `next`,
  // `totalGenerated`, `lastGenerated` for display — they hitchhike onto the
  // edited row but aren't DB columns.
  recurring_rules: new Set(['account', 'category', 'fromAccount', 'toAccount', 'next', 'totalGenerated', 'lastGenerated']),
  // `spent`/`pct` are display-only fields computed in the Budgets component;
  // they ride along on the edited row but are not DB columns.
  budgets:         new Set(['category', 'spent', 'pct']),
  // Budget engine rows get decorated by the compute layer with resolved display
  // fields (category name, computed line list, locked flags, etc.). Only real
  // columns are persisted; the rest ride along on in-memory objects.
  budget_template_lines: new Set(['category']),
  budget_overrides:      new Set(['category']),
  // Accounts and cards get decorated with live balances by their list
  // components (computeAccountLiveBalance / computeCardLiveBalance). Those
  // fields hitchhike onto the edited row but are derived, not DB columns.
  accounts:        new Set(['liveBalance']),
  cards:           new Set(['liveCurrentBalance']),
  // Loans get decorated with `liveBalance` (payment-driven amortised balance)
  // by loansWithLive; it's derived, not a DB column.
  loans:           new Set(['liveBalance']),
  // SavingsGoals decorates each goal with `current` (progress in goal ccy)
  // and `pct` (progress as %). Display-only, ride onto edit-mode form state.
  savings_goals:   new Set(['current', 'pct']),
  // Statements get decorated with computed payment progress (paid/remaining/pct)
  // and the card name for display — none are DB columns.
  card_statements: new Set(['paid', 'remaining', 'pct', 'cardName', 'status']),
};

// Inverse of TO_DB_RENAMES, lazy-built.
const FROM_DB_RENAMES_CACHE: Record<string, Record<string, string>> = {};
function fromDbRenames(table: string) {
  if (!FROM_DB_RENAMES_CACHE[table]) {
    const inv: Record<string, string> = {};
    for (const [k, v] of Object.entries(TO_DB_RENAMES[table] || {})) inv[v] = k;
    FROM_DB_RENAMES_CACHE[table] = inv;
  }
  return FROM_DB_RENAMES_CACHE[table];
}

function toDb(table: string, row: any): any {
  const renames = TO_DB_RENAMES[table] || {};
  const strip = STRIP_ON_WRITE[table] || new Set();
  const out: any = {};
  for (const [k, v] of Object.entries(row)) {
    if (strip.has(k)) continue;
    if (k === 'deletedAt' && v == null) continue; // let DB default null
    const dbKey = renames[k] ?? camelToSnake(k);
    out[dbKey] = v;
  }

  // Empty-string foreign keys (e.g. an unset toLoanId from a form default)
  // must become NULL — Postgres rejects '' for uuid columns.
  for (const k of Object.keys(out)) {
    if (k.endsWith('_id') && out[k] === '') out[k] = null;
  }

  // Per-table coercions for fields whose runtime type doesn't match the DB:
  // the budgets table stores month as a real date pinned to the 1st; the
  // artifact represents it as a 'YYYY-MM' string. Coerce here so callers
  // (BudgetModal, demo-loader, etc.) don't each need to know about it.
  if (table === 'budgets' && typeof out.month === 'string' && /^\d{4}-\d{2}$/.test(out.month)) {
    out.month = `${out.month}-01`;
  }
  // Budget engine month-like date columns follow the same 'YYYY-MM' convention.
  if (table === 'budget_overrides' && typeof out.month === 'string' && /^\d{4}-\d{2}$/.test(out.month)) {
    out.month = `${out.month}-01`;
  }
  if (table === 'budget_template_lines' && typeof out.effective_from === 'string' && /^\d{4}-\d{2}$/.test(out.effective_from)) {
    out.effective_from = `${out.effective_from}-01`;
  }

  return out;
}

function fromDb(table: string, row: any): any {
  if (row == null) return row;
  const inv = fromDbRenames(table);
  const out: any = {};
  for (const [k, v] of Object.entries(row)) {
    if (k.endsWith('_rel')) continue; // embedded relations handled separately
    const artifactKey = inv[k] ?? snakeToCamel(k);
    out[artifactKey] = v;
  }
  return out;
}

// ============ embedded SELECT recipes per table ============
// PostgREST joins to materialize the legacy name-string fields the artifact
// expects (tx.account, tx.category, tx.fromAccount, tx.toAccount, ...). The
// FK column is referenced via the `column!fk_name` disambiguator since
// transactions has four FKs into accounts.

const SELECT_BY_TABLE: Record<string, string> = {
  transactions: `*,
    account_rel:accounts!account_id(name),
    card_rel:cards!card_id(name),
    category_rel:categories!category_id(name, kind),
    from_account_rel:accounts!from_account_id(name),
    from_card_rel:cards!from_card_id(name),
    to_account_rel:accounts!to_account_id(name),
    to_card_rel:cards!to_card_id(name),
    from_investment_rel:investments!from_investment_id(symbol, name),
    to_investment_rel:investments!to_investment_id(symbol, name),
    from_loan_rel:loans!from_loan_id(name),
    to_loan_rel:loans!to_loan_id(name)
  `,
  recurring_rules: `*,
    account_rel:accounts!account_id(name),
    card_rel:cards!card_id(name),
    category_rel:categories!category_id(name)
  `,
  budgets: `*,
    category_rel:categories!category_id(name)
  `,
};

function enrich(table: string, dbRow: any): any {
  const out = fromDb(table, dbRow);

  if (table === 'transactions') {
    out.account     = dbRow.account_rel?.name      || dbRow.card_rel?.name      || '';
    out.category    = dbRow.category_rel?.name     || '';
    // A transfer leg may be an investment (buy/sell a holding) — fall back to
    // the holding's symbol so the legacy name-string display still resolves.
    out.fromAccount = dbRow.from_account_rel?.name || dbRow.from_card_rel?.name || dbRow.from_investment_rel?.symbol || dbRow.from_loan_rel?.name || '';
    out.toAccount   = dbRow.to_account_rel?.name   || dbRow.to_card_rel?.name   || dbRow.to_investment_rel?.symbol   || dbRow.to_loan_rel?.name   || '';
  } else if (table === 'recurring_rules') {
    out.account  = dbRow.account_rel?.name  || dbRow.card_rel?.name || '';
    out.category = dbRow.category_rel?.name || '';
  } else if (table === 'budgets') {
    out.category = dbRow.category_rel?.name || '';
    // The DB stores month as a date (YYYY-MM-01); the app compares months as
    // YYYY-MM strings (selectedMonth, monthBudgets filter). toDb does the
    // YYYY-MM -> YYYY-MM-01 coercion on write; this is the reverse on read.
    if (typeof out.month === 'string') out.month = out.month.slice(0, 7);
  } else if (table === 'budget_overrides') {
    // DB stores month as a date (YYYY-MM-01); the engine compares 'YYYY-MM'.
    if (typeof out.month === 'string') out.month = out.month.slice(0, 7);
  } else if (table === 'budget_template_lines') {
    if (typeof out.effectiveFrom === 'string') out.effectiveFrom = out.effectiveFrom.slice(0, 7);
  }
  // NOTE: previously enrich set `out.balance = out.openingBalance` (and the
  // same for cards/loans -> currentBalance). That was wrong: TO_DB_RENAMES
  // already remaps `opening_balance` -> `balance` / `currentBalance` on read,
  // so fromDb produces the correct field directly. `out.openingBalance` is
  // undefined here, and the enrich line overwrote the valid value with
  // undefined — causing every account/card/loan to show a 0 starting balance
  // after a refresh. The artifact-facing keys (`balance`, `currentBalance`)
  // are now whatever fromDb yielded.

  return out;
}

// ============ FX cache (for filling in transactions.amount_base) ============

let fxCache: Record<string, number> | null = null;
let fxCacheLoadedAt = 0;
// Latest rate (cached). Returns null when the currency has no rate at all, so
// callers can distinguish "missing" from a real 1:1 (AED) — don't silently
// treat an unknown currency as 1.0.
async function getLatestFxRate(code: string): Promise<number | null> {
  if (!fxCache || Date.now() - fxCacheLoadedAt > 60_000) {
    const { data } = await supabase.from('v_latest_fx').select('currency_code, rate_to_base');
    fxCache = {};
    for (const row of data || []) fxCache[row.currency_code] = Number(row.rate_to_base);
    fxCacheLoadedAt = Date.now();
  }
  return fxCache?.[code] ?? null;
}

// Rate as of a specific date — exchange_rates carries daily history, so a
// backdated tx (e.g. a statement import of a prior month) converts at the rate
// that applied THEN, not today's. Falls back to the latest rate if the date
// has no row yet.
async function getFxRateOn(code: string, dateStr?: string): Promise<number | null> {
  if (!code || code === 'AED') return 1;
  if (dateStr) {
    const { data } = await supabase
      .from('exchange_rates')
      .select('rate_to_base')
      .eq('currency_code', code)
      .lte('as_of_date', dateStr)
      .order('as_of_date', { ascending: false })
      .limit(1);
    if (data && data.length) return Number(data[0].rate_to_base);
  }
  return getLatestFxRate(code);
}

async function decorateTransactionForInsert(row: any): Promise<any> {
  const code = row.currency_code || 'AED';
  // Base-currency txs: amount_base is definitionally `amount` (rate = 1).
  // Always recompute it so editing `amount` can't leave a stale amount_base
  // behind — the bug where a bonus changed from 30k to 44,928 still read as
  // 30k everywhere the math used amount_base (snapshots, forecasts).
  if (code === 'AED') {
    return { ...row, fx_rate: 1, amount_base: Number(row.amount) || 0 };
  }
  // Foreign txs: preserve an already-locked historical rate (a statement
  // import sets amount_base + fx_rate at the rate that applied on the tx date,
  // not today's — don't clobber it).
  if (row.amount_base != null && row.fx_rate != null) return row;
  let rate = await getFxRateOn(code, row.date);
  if (rate == null) {
    // No rate for this currency on or before the tx date. Don't silently
    // pretend 1:1 (that corrupts amount_base for a foreign tx) — warn loudly
    // and fall back to 1 so the insert still succeeds; fx-refresh seeds 166
    // currencies daily, so this should be rare and self-heals next refresh.
    console.warn(`[FX] No rate for ${code} on/<= ${row.date || 'today'} — storing amount_base at 1:1; verify once a rate is available.`);
    rate = 1;
  }
  const amount = Number(row.amount) || 0;
  return {
    ...row,
    fx_rate: row.fx_rate ?? rate,
    amount_base: row.amount_base ?? Math.round(amount * rate * 10000) / 10000,
  };
}

// ============ user gate ============
// One getUser() call per session, cached. supabase.auth.getUser() is
// surprisingly serialized internally — calling it 320 times concurrently
// (Promise.all over a big demo dataset, say) wedges the auth client and
// freezes every write that's waiting on it. Resolve once, cache the id,
// invalidate on auth state changes.

let cachedUserId: string | null = null;
let userIdPromise: Promise<string | null> | null = null;
supabase.auth.onAuthStateChange((_event, session) => {
  cachedUserId = session?.user?.id ?? null;
  userIdPromise = null; // next call refetches
});

async function currentUserId(): Promise<string | null> {
  if (cachedUserId) return cachedUserId;
  if (userIdPromise) return userIdPromise;
  userIdPromise = supabase.auth.getUser().then(({ data }) => {
    cachedUserId = data.user?.id ?? null;
    return cachedUserId;
  });
  return userIdPromise;
}

// ============ public dataStore ============

export const dataStore = {
  async list(prefix: string): Promise<any[]> {
    // _schemaVersion is metadata; nothing to enumerate.
    if (prefix === '_schemaVersion') return [];

    const { prefix: p } = splitKey(prefix + 'x'); // tolerate raw prefix or full key
    if (!p) return [];

    const table = PREFIX_TO_TABLE[p];
    const select = SELECT_BY_TABLE[table] || '*';
    let q = supabase.from(table).select(select);

    // Skip the deleted_at filter on tables that don't carry the column.
    if (!TABLES_WITHOUT_DELETED_AT.has(table)) {
      q = q.is('deleted_at', null);
    }

    const { data, error } = await q;
    if (error) {
      console.error(`[dataStore] list(${prefix}) failed:`, error.message);
      return [];
    }
    return (data || []).map((r) => enrich(table, r));
  },

  async get(key: string): Promise<any | null> {
    // Schema-version stub: tell the artifact that all client-side migrations
    // (B1a seed, B2 backfill) are already done. M3 covers them server-side.
    if (key === '_schemaVersion') {
      return { version: 99, seededAt: '2026-05-17T00:00:00.000Z' };
    }

    const { prefix, pkValue } = splitKey(key);
    if (!prefix) return null;
    const table = PREFIX_TO_TABLE[prefix];
    const select = SELECT_BY_TABLE[table] || '*';

    const { data, error } = await supabase
      .from(table)
      .select(select)
      .eq(pkColumn(table), pkValue)
      .maybeSingle();

    if (error) {
      console.error(`[dataStore] get(${key}) failed:`, error.message);
      return null;
    }
    if (!data) return null;
    return enrich(table, data);
  },

  async set(key: string, obj: any): Promise<void> {
    // Schema-version stub: no-op. The DB doesn't track client-side migration versions.
    if (key === '_schemaVersion') return;

    const { prefix, pkValue } = splitKey(key);
    if (!prefix) throw new Error(`[dataStore] set: unknown prefix in key ${key}`);
    if (READ_ONLY_PREFIXES.has(prefix)) {
      // The artifact may attempt to seed currencies/fx on first load; we skip
      // because M3 already seeded them. The schema-version stub above also
      // prevents the gating block from running.
      return;
    }

    const table = PREFIX_TO_TABLE[prefix];
    let dbRow = toDb(table, obj);

    // Force the row's PK to match the key the caller used.
    dbRow[pkColumn(table)] = pkValue;

    // Attach user_id on insert if the caller didn't supply one (it usually
    // doesn't — the artifact never knew about RLS).
    if (!dbRow.user_id && table !== 'currencies' && table !== 'exchange_rates') {
      const uid = await currentUserId();
      if (!uid) throw new Error('[dataStore] set: not authenticated');
      dbRow.user_id = uid;
    }

    // Transactions need amount_base + fx_rate; compute if missing.
    if (table === 'transactions') {
      dbRow = await decorateTransactionForInsert(dbRow);
    }

    const { error } = await supabase
      .from(table)
      .upsert(dbRow, { onConflict: pkColumn(table) });

    if (error) {
      console.error(`[dataStore] set(${key}) failed:`, error.message);
      throw error;
    }
  },

  async remove(key: string): Promise<void> {
    if (key === '_schemaVersion') return;
    const { prefix, pkValue } = splitKey(key);
    if (!prefix) return;
    if (READ_ONLY_PREFIXES.has(prefix)) return;
    const table = PREFIX_TO_TABLE[prefix];
    const { error } = await supabase.from(table).delete().eq(pkColumn(table), pkValue);
    if (error) console.error(`[dataStore] remove(${key}) failed:`, error.message);
  },

  async clear(prefix: string): Promise<void> {
    const { prefix: p } = splitKey(prefix + 'x');
    if (!p || READ_ONLY_PREFIXES.has(p)) return;
    const table = PREFIX_TO_TABLE[p];
    const uid = await currentUserId();
    if (!uid) return;
    // Per-user wipe of one table. The auth user_id scope means clearAllData
    // can't accidentally touch a future second user's data.
    const { error } = await supabase.from(table).delete().eq('user_id', uid);
    if (error) console.error(`[dataStore] clear(${prefix}) failed:`, error.message);
  },
};

export const fetchAllByPrefix = async (prefix: string): Promise<any[]> => {
  return dataStore.list(prefix);
};

// ============ realtime ============
// Subscribe to INSERT/UPDATE/DELETE on a set of public tables and fire
// `onChange(table)` for each event. RLS gates delivery per-row to the signed-in
// user; we also add an explicit user_id filter once the id is known to cut
// cross-user noise (single-user today, but cheap and future-proof). The tables
// must be members of the `supabase_realtime` publication (see migration
// 20260711_realtime_publication.sql). Returns an unsubscribe function.
export const subscribeToUserChanges = (
  tables: string[],
  onChange: (table: string) => void,
): (() => void) => {
  const channel = supabase.channel(`floosi-rt-${Math.round((performance?.now?.() ?? 0))}-${tables.length}`);
  const attach = (uid: string | null) => {
    for (const table of tables) {
      channel.on(
        'postgres_changes' as any,
        uid
          ? { event: '*', schema: 'public', table, filter: `user_id=eq.${uid}` }
          : { event: '*', schema: 'public', table },
        () => onChange(table),
      );
    }
    channel.subscribe();
  };
  // Resolve the user id first so the server-side filter is applied; fall back to
  // an unfiltered (RLS-gated) subscription if it can't be resolved quickly.
  currentUserId().then(attach).catch(() => attach(null));
  return () => { supabase.removeChannel(channel); };
};

// Walks every live card installment, decrementing remaining_balance for any
// scheduled payment whose date has passed. Idempotent via
// last_decremented_through. Called both nightly by pg_cron and ad-hoc by the
// frontend right after creating an installment (so a freshly-created
// backfilled installment shows its catch-up progress without waiting for the
// next cron tick).
export const runInstallmentDecrement = async (): Promise<number> => {
  const { data, error } = await supabase.rpc('decrement_due_installments_all');
  if (error) {
    console.error('[dataStore] runInstallmentDecrement failed:', error.message);
    return 0;
  }
  return Number(data) || 0;
};

// Trash view: list soft-deleted rows for one prefix. Skips tables without a
// deleted_at column (they can't be "trashed").
export const listDeletedByPrefix = async (prefix: string): Promise<any[]> => {
  const { prefix: p } = splitKey(prefix + 'x');
  if (!p) return [];
  const table = PREFIX_TO_TABLE[p];
  if (TABLES_WITHOUT_DELETED_AT.has(table)) return [];
  const select = SELECT_BY_TABLE[table] || '*';
  const { data, error } = await supabase
    .from(table)
    .select(select)
    .not('deleted_at', 'is', null);
  if (error) {
    console.error(`[dataStore] listDeleted(${prefix}) failed:`, error.message);
    return [];
  }
  return (data || []).map((r) => enrich(table, r));
};
